import React, { useState, useRef, useEffect } from 'react';
import ScrollArea from './scrollArea';

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  options: DropdownOption[];
  label?: string;
  placeholder?: string;
  className?: string;
  otherValue?: string;
  allowOther?: boolean;
  onOtherChange?: (value: string) => void;
  isLoading?: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({
  name,
  value,
  onChange,
  options,
  label,
  placeholder = 'Select an option',
  className = '',
  otherValue = '',
  allowOther = false,
  onOtherChange,
  isLoading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showOtherInput, setShowOtherInput] = useState(value === 'other');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const otherInputRef = useRef<HTMLInputElement>(null);

  const handleOptionClick = (optionValue: string) => {
    const syntheticEvent = {
      target: {
        name,
        value: optionValue
      }
    } as React.ChangeEvent<HTMLSelectElement>;
    
    onChange(syntheticEvent);
    
    if (optionValue === 'other' && allowOther) {
      setShowOtherInput(true);
      setTimeout(() => otherInputRef.current?.focus(), 0);
    } else {
      setShowOtherInput(false);
      setIsOpen(false);
    }
  };

  const handleOtherInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (onOtherChange) {
      onOtherChange(newValue);
    }
    const syntheticEvent = {
      target: {
        name,
        value: 'other'
      }
    } as React.ChangeEvent<HTMLSelectElement>;
    onChange(syntheticEvent);
  };

  const handleOtherInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && otherValue.trim()) {
      e.preventDefault();
      if (onOtherChange) {
        onOtherChange(otherValue.trim());
      }
      const syntheticEvent = {
        target: {
          name,
          value: 'other'
        }
      } as React.ChangeEvent<HTMLSelectElement>;
      onChange(syntheticEvent);
      setIsOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);
  const displayValue = value === 'other' ? otherValue : selectedOption?.label || placeholder;

  return (
    <div className="relative" ref={dropdownRef}>
      {label && <label className="block mb-2 text-sm font-normal text-[#75777C]">{label}</label>}
      <div
        onClick={() => !isLoading && setIsOpen(!isOpen)}
        className={`relative h-[40px] cursor-pointer flex items-center justify-between px-4 text-left rounded-[10px] text-[#222222] text-sm placeholder:text-[#9FA1A7] focus:outline-none focus:ring-1 focus:ring-[#29DC5B] focus:border-transparent [box-shadow:0px_0px_0px_1px_#EEEFF1_inset] ${
          isLoading ? 'bg-[#F5F5F5] cursor-not-allowed' : 'bg-[#FFFFFF]'
        } ${className}`}
      >
        <span className={`truncate ${value ? 'text-[#222222]' : 'text-[#9FA1A7]'}`}>
          {isLoading ? 'Loading...' : displayValue}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && !isLoading && (
        <div className="absolute z-[1] w-full mt-1 bg-[#FFFFFF] text-left border border-[#EEEFF1] rounded-lg shadow-lg">
          <ScrollArea maxHeight="180px">
            <div className="p-2 space-y-1">
              {options.map((option) => (
                <div
                  key={option.value}
                  onClick={() => handleOptionClick(option.value)}
                  className={`px-3 py-2 cursor-pointer rounded-lg text-xs ${
                    value === option.value
                      ? 'bg-[#EEEFF1] text-[#222222]'
                      : 'hover:bg-[#FAFAFA] text-[#222222]'
                  }`}
                >
                  {option.label}
                </div>
              ))}
              {allowOther && (
                <div className="px-3 py-2">
                  <input
                    ref={otherInputRef}
                    type="text"
                    value={otherValue}
                    onChange={handleOtherInputChange}
                    onKeyDown={handleOtherInputKeyDown}
                    placeholder="Enter custom value"
                    className="w-full px-2 py-1 text-xs border border-[#EEEFF1] rounded-lg
                      focus:outline-none focus:ring-1 focus:ring-[#29DC5B] focus:border-transparent"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
